import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Flex,
  Text,
  Input,
  VStack,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { FaTimes } from 'react-icons/fa';
import { Room, User } from '../types';

interface GroupChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  users: User[];
  onCreateGroupChat: (userIds: string[], chatName: string) => void;
  bgColor: string;
  textColor: string;
  secondaryText: string;
  inputBg: string;
  hoverBg: string;
  PRIMARY_GREEN: string;
  rooms: Room[];
  onJoinDirectChat: (roomId: string) => void;
  createDirectChat: (userId: string) => void;
}

export const GroupChatDialog: React.FC<GroupChatDialogProps> = ({
  isOpen,
  onClose,
  users,
  onCreateGroupChat,
  bgColor,
  textColor,
  secondaryText,
  inputBg,
  hoverBg,
  PRIMARY_GREEN,
  rooms,
  onJoinDirectChat,
  createDirectChat,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [chatName, setChatName] = useState('');
  const [chatNameError, setChatNameError] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (user.username?.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)) &&
      !selectedUsers.some(selected => selected.id === user.id)
    );
  });

  const handleCreateGroupChat = () => {
    if (selectedUsers.length === 0) return;
    if (!chatName.trim()) {
      setChatNameError('Chat name is required');
      return;
    }
    if (selectedUsers.length === 1) {
      // create a direct chat, first check if the room already exists
      const room = rooms.find(r => r.type === 'direct' && r.members.includes(selectedUsers[0].id));
      if (room) {
        onJoinDirectChat(room.id);
        return;
      }
      else {
        createDirectChat(selectedUsers[0].id);
        setSelectedUsers([]);
        setSearchQuery('');
        setChatName('');
        setChatNameError('');
        onClose();
        return;
      }
    }
    onCreateGroupChat(selectedUsers.map(u => u.id), chatName.trim());
    setSelectedUsers([]);
    setSearchQuery('');
    setChatName('');
    setChatNameError('');
    onClose();
  };

  const handleChatNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChatName(e.target.value);
    if (e.target.value.trim()) {
      setChatNameError('');
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsSearchFocused(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor} color={textColor}>
        <ModalHeader>Create New Group Chat</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack gap={4}>
            <Box w="100%" position="relative" ref={dropdownRef}>
              <Text mb={2}>Invite Users:</Text>
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                bg={inputBg}
                mb={2}
                color={textColor}
                borderRadius="lg"
                _placeholder={{ color: secondaryText }}
              />
              {isSearchFocused && filteredUsers.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg={bgColor}
                  borderRadius="lg"
                  boxShadow="lg"
                  zIndex={1}
                  maxH="200px"
                  overflowY="auto"
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-track': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '24px',
                    },
                  }}
                >
                  {filteredUsers.map(user => (
                    <Box
                      key={user.id}
                      p={2}
                      cursor="pointer"
                      _hover={{ bg: hoverBg }}
                      onClick={() => {
                        setSelectedUsers([...selectedUsers, user]);
                        setSearchQuery('');
                        setIsSearchFocused(false);
                      }}
                    >
                      <Text>{user.username || user.email}</Text>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {selectedUsers.length > 0 && (
              <VStack w="100%" gap={2}>
                <Text fontSize="sm" color={secondaryText}>Selected Users:</Text>
                {selectedUsers.map(user => (
                  <Flex
                    key={user.id}
                    w="100%"
                    justify="space-between"
                    align="center"
                    p={2}
                    bg={hoverBg}
                    borderRadius="md"
                  >
                    <Text>{user.username || user.email}</Text>
                    <IconButton
                      aria-label="Remove user"
                      size="sm"
                      variant="ghost"
                      onClick={() => setSelectedUsers(selectedUsers.filter(u => u.id !== user.id))}
                      color={textColor}
                      icon={<FaTimes />}
                    />
                  </Flex>
                ))}
              </VStack>
            )}

            <Box w="100%">
              <Text mb={2}>Chat Name:</Text>
              <Input
                placeholder="Enter chat name..."
                value={chatName}
                onChange={handleChatNameChange}
                bg={inputBg}
                color={textColor}
                borderRadius="lg"
                _placeholder={{ color: secondaryText }}
                borderColor={chatNameError ? 'red.500' : undefined}
              />
              {chatNameError && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {chatNameError}
                </Text>
              )}
            </Box>

            <Button
              colorScheme="green"
              w="100%"
              mt={2}
              onClick={handleCreateGroupChat}
              isDisabled={selectedUsers.length === 0 || !chatName.trim()}
              bg={PRIMARY_GREEN}
              _hover={{ bg: '#004d1a' }}
            >
              Create A New Chat
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}; 